import type { ReactNode } from 'react';
import { cn } from '~/lib/utils';
import { ScrollRevealHeadline } from '~/landing_page/components/ScrollRevealHeadline';
import { ScrollRevealContent } from '~/landing_page/components/ScrollRevealContent';
import landingType from '~/landing_page/styles/landingTypography.module.css';
import styles from './PrivacySection.module.css';

export interface PrivacyFeature {
  iconSrc: string;
  label: string;
}

const DEFAULT_PRIVACY_FEATURES: PrivacyFeature[] = [
  { iconSrc: '/landing/privacy/icon-no-data-training.svg', label: 'No data training' },
  { iconSrc: '/landing/privacy/icon-read-only.svg', label: 'Read-only access' },
  { iconSrc: '/landing/privacy/icon-transparency.svg', label: 'Full transparency' },
];

export interface PrivacySectionProps {
  id?: string;
  className?: string;
  headline?: string;
  description?: string;
  features?: PrivacyFeature[];
  /** `null` for no photograph at all — a card that is only its backdrop. */
  backgroundSrc?: string | null;
  /**
   * Painted UNDER the plate, so it shows through wherever the plate carries alpha. v2 puts the
   * page's dust field here and the night sky fills with it; the light landing passes nothing.
   */
  backdrop?: ReactNode;
  /**
   * The black gradient over the plate. It buys white text its contrast on a bright photograph —
   * a dark plate does not need it, and it flattens one.
   */
  dim?: boolean;
}

export function PrivacySection({
  id,
  className,
  headline = 'Your work stays yours',
  description = "Deck AI reads your tools to generate decks. We're serious about keeping your context private.",
  features = DEFAULT_PRIVACY_FEATURES,
  backgroundSrc = '/landing/privacy/background.jpg',
  backdrop,
  dim = true,
}: PrivacySectionProps) {
  return (
    <section
      id={id}
      className={cn(landingType.sectionVerticalPadding, styles.section, className)}
      data-name="PrivacySection"
    >
      <div className={cn(landingType.sectionInset, styles.container)}>
        <div className={styles.card}>
          {(backgroundSrc || backdrop) && (
            <div className={styles.cardBg} aria-hidden>
              {backdrop}
              {backgroundSrc && <img className={styles.cardBgImage} src={backgroundSrc} alt="" />}
              {dim && <div className={styles.cardBgOverlay} />}
            </div>
          )}

          <div className={styles.content}>
            <ScrollRevealHeadline className={cn(landingType.sectionHeadline, styles.headline)}>
              {headline}
            </ScrollRevealHeadline>
            <ScrollRevealContent as="p" className={cn(landingType.sectionBody, styles.description)}>
              {description}
            </ScrollRevealContent>
          </div>

          <ul className={styles.featureList} role="list">
            {features.map((feature, index) => (
              <li key={`${feature.label}-${index}`} className={styles.featureItem}>
                <img className={styles.featureIcon} src={feature.iconSrc} alt="" aria-hidden />
                <span className={styles.featureLabel}>{feature.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

PrivacySection.displayName = 'PrivacySection';
